<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  NButton,
  NCollapse,
  NCollapseItem,
  NDrawer,
  NDrawerContent,
  NFlex,
  NIcon,
  NTag,
  NTooltip,
} from 'naive-ui';
import { IconAdd, IconCheck, IconCopy } from '@/components/design/icons';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaSwitch from '@/components/design/base/GaiaSwitch.vue';
import {
  gaiaAccent,
  gaiaFontSize,
  gaiaFonts,
  gaiaTracking,
  gaiaFontWeight,
  gaiaNeutral,
  gaiaStatus,
  gaiaTemplating,
} from '@/components/design/themes/tokens';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import {
  type ControlFlowSnippet,
  makeBranchLocalId,
  renderControlFlowSnippet,
} from '@/composables/contractGeneration/useControlFlowSnippets';

/**
 * Editor for a single control-flow snippet (a `{% if %}` conditional or a
 * `{% for %}` loop). Mirrors AnnotationConfigPopover's drawer design — a
 * right-hand drawer with a live preview pinned to the bottom — but the preview
 * is computed locally since control flow is deterministic and never needs a
 * backend round-trip.
 */

interface Props {
  show: boolean;
  /** The snippet being edited. Drives the whole form. */
  snippet: ControlFlowSnippet | null;
  /** True when editing an existing snippet — surfaces the Remove button. */
  editing?: boolean;
}

interface Emits {
  (e: 'update:show', value: boolean): void;
  (e: 'apply', snippet: ControlFlowSnippet): void;
  (e: 'remove'): void;
}

const props = withDefaults(defineProps<Props>(), {
  editing: false,
});
const emit = defineEmits<Emits>();

const showDrawer = computed({
  get: () => props.show,
  set: value => {
    emit('update:show', value);
  },
});

// Local editable copy — committed back to the parent only on Apply.
const draft = ref<ControlFlowSnippet | null>(null);

watch(
  () => [props.show, props.snippet?.local_id],
  () => {
    if (!props.show || !props.snippet) return;
    // Deep-clone so edits don't mutate the parent's snippet until Apply.
    draft.value = {
      ...props.snippet,
      branches: props.snippet.branches.map(branch => ({ ...branch })),
    };
  },
  { immediate: true }
);

const isLoop = computed(() => draft.value?.kind === 'loop');
const isConditional = computed(() => draft.value?.kind === 'conditional');

// Scope as a single toggle: on = wrap whole paragraphs (%p, clean output —
// no blank lines from the tags); off = inline within the line (a condition
// inside a sentence). Surfaced under "Advanced options".
const wholeParagraph = computed<boolean>({
  get: () => draft.value?.scope === 'paragraph',
  set: value => {
    if (draft.value) draft.value.scope = value ? 'paragraph' : 'inline';
  },
});

const drawerTitle = computed<string>(() =>
  draft.value?.kind === 'loop' ? 'Repeat section' : 'Conditional section'
);

const drawerWidth = computed<number>(() => {
  if (typeof window === 'undefined') return 560;
  // Capped at the viewport so narrow hosts (the Word task pane) get a
  // full-width sheet instead of an overflowing panel.
  return Math.min(620, Math.max(420, Math.round(window.innerWidth * 0.4)), window.innerWidth);
});

const preview = computed<string>(() => (draft.value ? renderControlFlowSnippet(draft.value) : ''));

// --- Conditional branch editing -------------------------------------

function updateBranchExpression(index: number, value: string): void {
  if (!draft.value) return;
  draft.value.branches = draft.value.branches.map((branch, i) =>
    i === index ? { ...branch, expression: value } : branch
  );
}

function addElifBranch(): void {
  if (!draft.value) return;
  draft.value.branches = [
    ...draft.value.branches,
    { local_id: makeBranchLocalId(), expression: '' },
  ];
}

function removeBranch(index: number): void {
  if (!draft.value) return;
  // The first branch is the `if` and can't be removed — only `elif`s can.
  if (index === 0) return;
  draft.value.branches = draft.value.branches.filter((_, i) => i !== index);
}

function branchLabel(index: number): string {
  return index === 0 ? 'If' : 'Else if';
}

function branchKeyword(index: number): string {
  return index === 0 ? 'if' : 'elif';
}

// --- Copy ------------------------------------------------------------

const copied = ref<boolean>(false);
let copiedTimer: number | null = null;

async function copyPreview(): Promise<void> {
  const text = preview.value;
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    copied.value = true;
    if (copiedTimer !== null) window.clearTimeout(copiedTimer);
    copiedTimer = window.setTimeout(() => {
      copied.value = false;
    }, 1500);
  } catch {
    // Silently ignore — the preview is still on screen to copy manually.
  }
}

// --- Apply / remove --------------------------------------------------

function applyAndClose(): void {
  if (!draft.value) return;
  emit('apply', {
    ...draft.value,
    branches: draft.value.branches.map(branch => ({ ...branch })),
  });
  emit('update:show', false);
}

function removeAndClose(): void {
  emit('remove');
  emit('update:show', false);
}
/**
 * Inline custom properties on the drawer content rather than `v-bind()`: the
 * drawer teleports to <body>, so component-root CSS vars never reach it.
 */
const drawerStyleVars = {
  '--tplcfg-fs-meta': gaiaFontSize.meta,
  '--tplcfg-fs-nano': gaiaFontSize.nano,
  '--tplcfg-fw-medium': String(gaiaFontWeight.medium),
  '--tplcfg-fw-semibold': String(gaiaFontWeight.semibold),
  '--tplcfg-fw-bold': String(gaiaFontWeight.bold),
  '--tplcfg-track-caps': gaiaTracking.labelSoft,
  '--tplcfg-mono': gaiaFonts.mono,
  '--tplcfg-surface': gaiaNeutral.surface,
  '--tplcfg-soft': gaiaNeutral.surfaceSoft,
  '--tplcfg-muted': gaiaNeutral.surfaceMuted,
  '--tplcfg-border': gaiaNeutral.borderCard,
  '--tplcfg-faint': gaiaNeutral.textFaint,
  '--tplcfg-subtle': gaiaNeutral.textSubtle,
  '--tplcfg-text': gaiaNeutral.text,
  '--tplcfg-ink': gaiaNeutral.ink,
  '--tplcfg-accent': gaiaTemplating.customFg,
  '--tplcfg-accent-strong': gaiaAccent.strong,
  '--tplcfg-accent-border': gaiaTemplating.customBorder,
  '--tplcfg-accent-tint': gaiaTemplating.customSurface,
  '--tplcfg-accent-wash': gaiaAccent.wash,
  '--tplcfg-entity-border': gaiaTemplating.entityBorder,
  '--tplcfg-entity-fg': gaiaTemplating.entityFg,
  '--tplcfg-block-fg': gaiaTemplating.blockFg,
  '--tplcfg-block-surface': gaiaTemplating.blockSurface,
  '--tplcfg-alert': gaiaStatus.alert,
  '--tplcfg-alert-surface': gaiaStatus.alertSurface,
  '--tplcfg-alert-border': gaiaStatus.alertBorder,
};
</script>

<template>
  <n-drawer
    v-model:show="showDrawer"
    :width="drawerWidth"
    placement="right"
    :mask-closable="true"
    :auto-focus="false"
    class="cf-drawer"
  >
    <n-drawer-content
      closable
      :native-scrollbar="false"
      :body-content-style="{ padding: '0' }"
      :style="drawerStyleVars"
    >
      <template #header>
        <span class="cf-drawer__title">
          {{ drawerTitle }}
          <n-tag size="small" round :bordered="false" type="info" class="cf-drawer__origin-tag">
            {{ editing ? 'Edit' : 'New' }}
          </n-tag>
        </span>
      </template>

      <n-flex v-if="draft" vertical class="cf-drawer__body" :size="16">
        <!-- Loop: name-each-item + list -->
        <section v-if="isLoop" class="cf-drawer__binding">
          <div class="cf-drawer__binding-summary">
            <span class="cf-drawer__label">Section</span>
            <code class="cf-drawer__binding-path">Repeat for each item in a list</code>
          </div>
          <label class="cf-drawer__field">
            <span class="cf-drawer__label">Name each item as</span>
            <gaia-input v-model:value="draft.loopVar" placeholder="e.g. service" size="medium" />
            <span class="cf-drawer__field-hint">
              How you'll refer to a single item inside the section.
            </span>
          </label>
          <div class="cf-drawer__inline-keyword">
            <span class="cf-drawer__keyword-pill">in</span>
          </div>
          <label class="cf-drawer__field">
            <span class="cf-drawer__label">List to repeat</span>
            <gaia-input v-model:value="draft.iterable" placeholder="e.g. services" size="medium" />
            <span class="cf-drawer__field-hint">
              The list of items the section should loop over. The section closes automatically with
              <code>{% endfor %}</code>.
            </span>
          </label>
        </section>

        <!-- Conditional: if / elif branches + else toggle -->
        <template v-else-if="isConditional">
          <section class="cf-drawer__binding">
            <div class="cf-drawer__binding-summary">
              <span class="cf-drawer__label">Section</span>
              <code class="cf-drawer__binding-path">Show content only when a condition is met</code>
            </div>
            <p class="cf-drawer__field-hint">
              Each branch is checked in order; the first one that's true is shown. The section
              closes automatically with <code>{% endif %}</code>.
            </p>
          </section>

          <section class="cf-drawer__section">
            <header class="cf-drawer__section-header">
              <span class="cf-drawer__section-title">
                Branches
                <span class="cf-drawer__count-pill">{{ draft.branches.length }}</span>
              </span>
            </header>

            <n-flex vertical class="cf-branches" :size="10">
              <div
                v-for="(branch, index) in draft.branches"
                :key="branch.local_id"
                class="cf-branch"
              >
                <div class="cf-branch__head">
                  <span class="cf-branch__keyword">{{ branchKeyword(index) }}</span>
                  <span class="cf-branch__label">{{ branchLabel(index) }}</span>
                  <gaia-delete-button
                    v-if="index > 0"
                    intent="remove"
                    size="xs"
                    subject="branch"
                    @click="removeBranch(index)"
                  />
                </div>
                <gaia-input
                  :value="branch.expression"
                  :placeholder="index === 0 ? 'e.g. is_vip_client' : 'e.g. total > 1000'"
                  size="medium"
                  @update:value="value => updateBranchExpression(index, value)"
                />
                <span class="cf-branch__hint">
                  Included only when this is true at render time.
                </span>
              </div>

              <n-button size="small" dashed block class="cf-branches__add" @click="addElifBranch">
                <template #icon>
                  <n-icon><IconAdd /></n-icon>
                </template>
                Add else-if branch
              </n-button>
            </n-flex>
          </section>

          <section class="cf-drawer__section">
            <div class="cf-drawer__toggle-row">
              <div class="cf-drawer__toggle-text">
                <span class="cf-drawer__label">Otherwise (else) branch</span>
                <span class="cf-drawer__field-hint">
                  Shown when none of the branches above are true.
                </span>
              </div>
              <gaia-switch v-model:value="draft.hasElse" />
            </div>
          </section>
        </template>

        <!-- Advanced: %p whole-paragraph toggle -->
        <n-collapse class="cf-drawer__advanced">
          <n-collapse-item name="advanced" title="Advanced options">
            <label class="cf-drawer__field">
              <n-flex align="center" justify="space-between" :wrap="false">
                <span class="cf-drawer__label">Apply to the whole paragraph</span>
                <gaia-switch v-model:value="wholeParagraph" size="small" />
              </n-flex>
              <span class="cf-drawer__field-hint">
                On — the tags wrap whole paragraphs and the output stays clean (no blank lines left
                behind). Turn off only for a condition inside a sentence.
              </span>
            </label>
          </n-collapse-item>
        </n-collapse>

        <section class="cf-drawer__section cf-drawer__output">
          <header class="cf-drawer__output-head">
            <span class="cf-drawer__section-title">Live preview</span>
            <n-tooltip placement="top" :delay="200">
              <template #trigger>
                <n-button
                  size="tiny"
                  quaternary
                  class="cf-drawer__copy-btn"
                  :type="copied ? 'success' : 'default'"
                  @click="copyPreview"
                >
                  <template #icon>
                    <n-icon>
                      <IconCheck v-if="copied" />
                      <IconCopy v-else />
                    </n-icon>
                  </template>
                  {{ copied ? 'Copied' : 'Copy' }}
                </n-button>
              </template>
              Copy this snippet to your clipboard
            </n-tooltip>
          </header>
          <pre class="cf-drawer__preview">{{ preview }}</pre>
        </section>
      </n-flex>

      <template #footer>
        <n-flex class="cf-drawer__footer" justify="space-between" align="center" :size="12">
          <gaia-delete-button
            v-if="editing"
            intent="remove"
            variant="label"
            @click="removeAndClose"
          />
          <span v-else aria-hidden="true" />
          <n-flex class="cf-drawer__footer-actions" :size="8">
            <n-button size="small" @click="$emit('update:show', false)">Cancel</n-button>
            <n-button size="small" type="primary" @click="applyAndClose">
              {{ editing ? 'Apply' : 'Save snippet' }}
            </n-button>
          </n-flex>
        </n-flex>
      </template>
    </n-drawer-content>
  </n-drawer>
</template>

<style scoped>
.cf-drawer__title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: var(--tplcfg-fw-semibold);
}

.cf-drawer__origin-tag {
  font-size: var(--tplcfg-fs-nano) !important;
  font-weight: var(--tplcfg-fw-semibold);
  letter-spacing: var(--tplcfg-track-caps);
}

/* Column layout/gap handled by NFlex; padding kept. */
.cf-drawer__body {
  padding: 18px 20px 24px;
}

.cf-drawer__section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cf-drawer__section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.cf-drawer__section-title {
  font-size: var(--tplcfg-fs-meta);
  font-weight: var(--tplcfg-fw-bold);
  text-transform: uppercase;
  letter-spacing: var(--tplcfg-track-caps);
  color: var(--tplcfg-text);
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.cf-drawer__count-pill {
  font-size: var(--tplcfg-fs-nano);
  font-weight: var(--tplcfg-fw-bold);
  color: var(--tplcfg-accent);
  background: var(--tplcfg-accent-tint);
  padding: 1px 7px;
  border-radius: 999px;
  letter-spacing: 0;
  text-transform: none;
  line-height: 1.4;
}

.cf-drawer__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cf-drawer__label {
  font-size: var(--tplcfg-fs-meta);
  font-weight: var(--tplcfg-fw-semibold);
  text-transform: uppercase;
  letter-spacing: var(--tplcfg-track-caps);
  color: var(--tplcfg-subtle);
}

.cf-drawer__field-hint {
  font-size: var(--tplcfg-fs-meta);
  color: var(--tplcfg-subtle);
  line-height: 1.4;
}

.cf-drawer__field-hint code,
.cf-drawer__binding-path code {
  background: var(--tplcfg-muted);
  padding: 1px 5px;
  border-radius: 4px;
  font-size: var(--tplcfg-fs-meta);
  font-family: var(--tplcfg-mono);
  color: var(--tplcfg-ink);
}

.cf-drawer__binding {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
  background: var(--tplcfg-accent-wash);
  border: 1px solid var(--tplcfg-accent-tint);
  border-radius: 10px;
}

.cf-drawer__binding-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.cf-drawer__binding-path {
  font-family: var(--tplcfg-mono);
  font-size: var(--tplcfg-fs-meta);
  background: var(--tplcfg-surface);
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid var(--tplcfg-accent-border);
  color: var(--tplcfg-accent-strong);
  flex: 1 1 auto;
  min-width: 0;
  word-break: break-word;
  white-space: normal;
  line-height: 1.4;
}

.cf-drawer__inline-keyword {
  display: flex;
  justify-content: center;
  margin: -4px 0;
}

.cf-drawer__keyword-pill {
  font-family: var(--tplcfg-mono);
  font-size: var(--tplcfg-fs-meta);
  font-weight: var(--tplcfg-fw-semibold);
  letter-spacing: var(--tplcfg-track-caps);
  color: var(--tplcfg-text);
  background: var(--tplcfg-muted);
  border: 1px solid var(--tplcfg-border);
  padding: 2px 12px;
  border-radius: 999px;
}

.cf-drawer__toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: var(--tplcfg-soft);
  border: 1px solid var(--tplcfg-border);
  border-radius: 10px;
}

.cf-drawer__toggle-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

/* ─── Conditional branches ─────────────────────────────────────────── */

/* Column layout/gap handled by NFlex. */

.cf-branch {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  background: var(--tplcfg-soft);
  border: 1px solid var(--tplcfg-border);
  border-radius: 10px;
}

.cf-branch__head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cf-branch__keyword {
  font-family: var(--tplcfg-mono);
  font-size: var(--tplcfg-fs-meta);
  font-weight: var(--tplcfg-fw-semibold);
  color: var(--tplcfg-accent);
  background: var(--tplcfg-accent-wash);
  border: 1px solid var(--tplcfg-accent-tint);
  padding: 1px 8px;
  border-radius: 999px;
}

.cf-branch__label {
  font-size: var(--tplcfg-fs-meta);
  font-weight: var(--tplcfg-fw-bold);
  text-transform: uppercase;
  letter-spacing: var(--tplcfg-track-caps);
  color: var(--tplcfg-text);
  flex: 1 1 auto;
}

.cf-branch__hint {
  font-size: var(--tplcfg-fs-meta);
  color: var(--tplcfg-subtle);
  line-height: 1.4;
}

.cf-branches__add {
  margin-top: 2px;
}

/* ─── Output preview ───────────────────────────────────────────────── */

.cf-drawer__output {
  position: sticky;
  bottom: -24px;
  background: var(--tplcfg-surface);
  padding-top: 8px;
  border-top: 1px dashed var(--tplcfg-border);
  margin-top: 4px;
}

.cf-drawer__output-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.cf-drawer__copy-btn {
  font-size: var(--tplcfg-fs-meta);
  font-weight: var(--tplcfg-fw-medium);
}

.cf-drawer__preview {
  margin: 0;
  padding: 10px 12px;
  background: var(--tplcfg-soft);
  border: 1px solid var(--tplcfg-border);
  color: var(--tplcfg-ink);
  border-radius: 8px;
  font-family: var(--tplcfg-mono);
  font-size: var(--tplcfg-fs-meta);
  line-height: 1.55;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

/* ─── Footer ───────────────────────────────────────────────────────── */

/* Layout handled by NFlex; full-width footer kept. */
.cf-drawer__footer {
  width: 100%;
}
</style>
